<template>
    <div class="relative flex flex-col max-w-screen-xl mx-auto pl-5 lg:pl-12 pr-5 py-5 lg:py-12">
        <regions-modal></regions-modal>

        <lead-header>
            Final Step: Get connected with a licensed local lawyer
        </lead-header>

        <input type="hidden" ref="languageId" name="language_id_bcp47" v-model="form.language_id_bcp47">

        <div class="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2">
            <div>
                <input
                    ref="firstName"
                    class="w-full p-4 bg-white border border-zinc-300 rounded-lg outline-brand-blue"
                    :class="{ 'border-red-500 focus:outline-red-500' : form.errors.has('first_name') }"
                    type="text"
                    name="first_name"
                    placeholder="First Name"
                    minlength="2"
                    required
                    data-hj-allow
                    v-model="form.first_name"
                    @keydown="clear($event.target.name)"
                    @keydown.enter="validateAndSubmit"
                    @blur="validateInput($event.target)"
                    autofocus="true"
                >

                <div
                    class="mt-2 text-left text-red-500 alert alert-danger"
                    role="alert"
                    v-if="form.errors.has('first_name')"
                >
                    <span class="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>

                    <span class="sr-only">Error:</span>

                    {{ form.errors.get('first_name') }}
                </div>
            </div>

            <div>
                <input
                    ref="lastName"
                    class="w-full p-4 bg-white border border-zinc-300 rounded-lg outline-brand-blue"
                    :class="{ 'border-red-500 focus:outline-red-500' : form.errors.has('last_name') }"
                    type="text"
                    name="last_name"
                    placeholder="Last Name"
                    minlength="2"
                    required
                    data-hj-allow
                    v-model="form.last_name"
                    @keydown="clear($event.target.name)"
                    @keydown.enter="validateAndSubmit"
                    @blur="validateInput($event.target)"
                >

                <div
                    class="mt-2 text-left text-red-500 alert alert-danger"
                    role="alert"
                    v-if="form.errors.has('last_name')"
                >
                    <span class="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>

                    <span class="sr-only">Error:</span>

                    {{ form.errors.get('last_name') }}
                </div>
            </div>

            <div class="md:col-span-2">
                <div class="flex items-center">
                    <label for="phone" class="px-3 py-4 bg-white border border-r-0 border-zinc-300 rounded-l-lg outline-brand-blue flex-shrink flex items-center space-x-2"
                         :class="{ 'border-red-500 focus:outline-red-500' : form.errors.has('phone') }">
                        <USFlagIcon/>
                        <span>+1</span>
                    </label>
                    <input
                        id="phone"
                        ref="phone"
                        class="w-full pl-3 p-4 bg-white border border-zinc-300 rounded-r-lg outline-brand-blue"
                        :class="{ 'border-red-500 focus:outline-red-500' : form.errors.has('phone') }"
                        type="text"
                        name="phone"
                        placeholder="Mobile Phone"
                        minlength="10"
                        maxlength="15"
                        required
                        v-model="form.phone"
                        @keydown="clear($event.target.name); clear('error')"
                        @keydown.enter="validateAndSubmit"
                        @blur="validatePhone($event.target, true, true)"
                    >
                </div>

                <div
                    class="mt-2 text-left text-red-500 alert alert-danger"
                    role="alert"
                    v-if="form.errors.has('phone')"
                >
                    <span class="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>

                    <span class="sr-only">Error:</span>

                    {{ form.errors.get('phone') }}
                </div>
            </div>

            <div class="md:col-span-2">
                <input
                    ref="email"
                    class="w-full p-4 bg-white border border-zinc-300 rounded-lg outline-brand-blue"
                    :class="{ 'border-red-500 focus:outline-red-500' : form.errors.has('email') }"
                    type="email"
                    name="email"
                    placeholder="Email"
                    required
                    v-model="form.email"
                    @keydown="clear($event.target.name); clear('error')"
                    @keydown.enter="validateAndSubmit"
                    @blur="validateInput($event.target)"
                >

                <div
                    class="mt-2 text-left text-red-500 alert alert-danger"
                    role="alert"
                    v-if="form.errors.has('email')"
                >
                    <span class="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>

                    <span class="sr-only">Error:</span>

                    {{ form.errors.get('email') }}
                </div>
            </div>
        </div>

        <div
            class="mt-2 mb-4 text-center text-red-500 alert alert-danger"
            role="alert"
            v-if="form.errors.has('error')"
        >
            <span class="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>

            <span class="sr-only">Error:</span>

            <span v-html="form.errors.errors.error"></span>
        </div>

        <div class="mt-3 text-sm text-neutral-900/50">
            <input
                type="hidden"
                id="sms_opted_in"
                name="sms_opted_in"
                value="1"
                v-model="form.sms_opted_in"
            >

            <p>By continuing, you agree to be contacted about this request via text message.</p>
        </div>

        <div class="mt-5 lg:mt-6 flex flex-col lg:flex-row justify-between w-full items-center">
            <previous-button></previous-button>

            <next-button
                type="submit"
                id="btn-lead-capture-step-3"
                @click.native="validateAndSubmit"
                :class="{ 'bg-brand-blue-darker': submitting }"
                :disabled="submissionDisabled"
            >
                Find me a Lawyer
            </next-button>
        </div>
        <TrustpilotMicroCombo/>
    </div>
</template>

<script>
import {set} from "lodash";
import {mapGetters} from 'vuex';

import PreviousButton from "./PreviousButton.vue";
import NextButton from './NextButton.vue';
import LeadHeader from "./LeadHeader.vue";
import RegionsModal from './RegionsModal.vue';
import USFlagIcon from "./USFlagIcon.vue";
import TrustpilotMicroCombo from "./TrustpilotMicroCombo.vue";

export default {
    components: {
        USFlagIcon,
        PreviousButton,
        NextButton,
        LeadHeader,
        RegionsModal,
        TrustpilotMicroCombo
    },

    data: () => ({
        lastValidatedPhone: null,
        phoneType: null,
        submitting: false
    }),

    computed: {
        ...mapGetters('lead', ['form']),

        /**
         * @returns {boolean}
         */
        submissionDisabled() {
            return this.form.errors.onlyDisplayableErrors()
                || this.submitting;
        },
    },

    methods: {
        clear(field) {
            this.$delete(this.form.errors.errors, field);
        },

        /**
         * Record necessary location information in local storage.
         */
        recordInLocalStorage() {
            let form = JSON.parse(window.localStorage.getItem('lcsForm'));

            set(form, 'email', this.form.email);
            set(form, 'first_name', this.form.first_name);
            set(form, 'last_name', this.form.last_name);
            set(form, 'phone', this.form.phone);
            set(form, 'phone_alt', this.form.phone_alt);
            set(form, 'phone_type', this.form.phone_type);
            set(form, 'sms_opted_in', this.form.sms_opted_in);
            set(form, 'language_bcp47', window.navigator.language);

            window.localStorage.setItem('lcsForm', JSON.stringify(form));
        },

        async validateAndSubmit() {
            const { phone, ...otherRefs } = this.$refs;

            await this.validatePhone(phone, true, true);
            Object.values(otherRefs).forEach(ref => this.validateInput(ref));

            if (this.submissionDisabled) {
                return;
            }

            this.submit();
        },

        /**
         * Record data in localStorage and fire event that this step is done.
         */
        submit() {
            if (this.submissionDisabled) {
                return;
            }

            this.recordInLocalStorage();

            window.dataLayer.push({
                event: 'form_step_submit',
                form_step_id: window.getCurrentStep(),
                form_step_name: 'contact'
            });

            this.$event.$emit('step:next');
        },

        /**
         * Validate the given input field and apply css class if valid.
         *
         * @param {object} target
         */
        validateInput(target) {
            target.classList.remove('is-valid');

            if (target.checkValidity()) {
                target.classList.add('is-valid');
            } else {
                this.$set(this.form.errors.errors, target.name, ['Please provide a valid input.']);
            }
        },


        /**
         * Remove characters to check just the number.
         *
         * @param {string} phoneNumber
         */
        removeNonNumericCharacters(phoneNumber) {
            return phoneNumber.replace(/\D/g, '');
        },

        /**
         * Check phone number length.
         *
         * @param {string} phoneNumber
         * @param {object} target
         * @param {string} field
         */
        validatePhoneNumberLength(phoneNumber, target, field) {
            if (phoneNumber.length > 10) {
                this.setPhoneError(target, field, 'Please enter only 10 digits, without country code.');
                return false;
            }

            if (phoneNumber.length < 10) {
                this.setPhoneError(target, field, 'Please enter a 10 digit number, without country code.');
                return false;
            }

            return true;
        },

        /**
         * See if a phone number is in the correct format.
         *
         * @param {string} phoneNumber
         * @param {object} target
         * @param {string} field
         */
        validatePhoneNumberFormat(phoneNumber, target, field) {
            const pregMatch = RegExp('^[0-9]{10}');

            if (!pregMatch.test(phoneNumber)) {
                this.setPhoneError(target, field);
                return false;
            }

            return true;
        },

        /**
         * See if a phone number appears to be valid.
         *
         * @param {object} target
         * @param {boolean} required
         * @param {boolean} smsable
         */
        async validatePhone(target, required = false, smsable = false) {
            const field = target.name;

            if (target.value === this.lastValidatedPhone) {
                return;
            }

            target.classList.remove('is-valid');
            target.classList.remove('has-error');

            if (target.value === '' && required) {
                this.setPhoneError(target, field, 'Please provide a 10 digit mobile number');
                return;
            }

            if (target.value === '' && !required) {
                target.classList.add('is-valid');
                return;
            }

            const phone = this.removeNonNumericCharacters(target.value);

            if (!this.validatePhoneNumberLength(phone, target, field)) {
                return;
            }

            if (!this.validatePhoneNumberFormat(phone, target, field)) {
                return;
            }

            if (!smsable) {
                target.classList.add('is-valid');
                return;
            }

            await axios.post(route('api.validate.smsable'), {phone})
                .then(({data}) => {
                    if (data.sms_able) {
                        target.classList.add('is-valid');

                        this.$store.commit('lead/updateForm', {
                            phone_type: data.phone_type
                        });

                        this.lastValidatedPhone = target.value;

                        return;
                    }

                    this.setPhoneError(target, 'phone', 'Please provide a valid mobile number.');
                })
                .catch(() => {
                    this.setPhoneError(target, 'phone');
                });
        },

        /**
         * Set proper error on phone field.
         *
         * @param {object} target
         * @param {string} field
         * @param {string} message
         */
        setPhoneError(target, field = 'phone', message = 'Please provide a 10 digit mobile number.') {
            target.classList.add('has-error');
            this.$set(this.form.errors.errors, field, [message]);
        },

        focusFirstNameInput () {
            window.setTimeout(() => {
                this.$refs.firstName.focus();
            }, 100);
        },
    },

    mounted() {
        this.focusFirstNameInput();

        if (!form.language_id_bcp47) {
            this.$refs.languageId.value = window.navigator.language;
            this.form.language_id_bcp47 = window.navigator.language;
        }

        window.dataLayer.push({
            event: 'form_step_mounted',
            form_step_id: window.getCurrentStep(),
            form_step_name: 'contact'
        });
    }
};
</script>
